"use client";

import React from "react";
import { useState, useEffect } from "react";
import CommentInput from "./CommentInput";
import Comment from "./Comment";
import {
  postComments,
  getComments,
  removeComments,
  toggleCommentLike,
  getCommentLikes
} from "../../services/addComment";
import { useNavigate } from "react-router-dom";
import { successToast, errorToast } from "../../utils/toasters";
import { isAuthenticated, getUserId } from "../../utils/helpers";

const CommentSection = React.forwardRef(({ scoopid, onCommentCountChange, postid, autoFetchCount }, ref) => {
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Helper function to fetch like data for a comment
  const fetchCommentLikes = async commentId => {
    try {
      let response;
      if (isAuthenticated()) {
        const userid = getUserId();
        if (userid) {
          response = await getCommentLikes(commentId, userid);
        } else {
          response = await getCommentLikes(commentId);
        }
      } else {
        response = await getCommentLikes(commentId);
      }

      if (response.status === "success" && response.data) {
        return {
          count: parseInt(response.data.totalCount) || 0,
          likedByCurrentUser: response.data.likedByCurrentUser || false
        };
      }
    } catch (error) {
      console.error(`Failed to fetch likes for comment ${commentId}:`, error);
    }
    return { count: 0, likedByCurrentUser: false };
  };

  // Helper function to format comments with like data
  const formatCommentsWithLikes = async comments => {
    const formattedComments = [];

    for (const comment of comments) {
      const likeData = await fetchCommentLikes(comment.id);

      const formattedReplies = [];
      if (Array.isArray(comment.replies)) {
        for (const reply of comment.replies) {
          const replyLikeData = await fetchCommentLikes(reply.id);
          formattedReplies.push({
            id: reply.id,
            author: {
              name: reply.username || "Unknown User",
              username: reply.username || "unknown",
              avatar: reply.avatar_url
            },
            content: reply.comment,
            timestamp: formatDate(reply.createddate),
            likes: replyLikeData.count,
            likedByCurrentUser: replyLikeData.likedByCurrentUser,
            image: reply.photo_url || null
          });
        }
      }

      formattedComments.push({
        id: comment.id,
        author: {
          name: comment.username || "Unknown User",
          username: comment.username || "unknown",
          avatar: comment.avatar_url
        },
        content: comment.comment,
        timestamp: formatDate(comment.createddate),
        likes: likeData.count,
        likedByCurrentUser: likeData.likedByCurrentUser,
        image: comment.photo_url || null,
        replies: formattedReplies
      });
    }

    return formattedComments;
  };

  // Fetch comments when component mounts or postid changes
  useEffect(
    () => {
      const fetchComments = async () => {
        if (!postid) return;

        setIsLoading(true);
        try {
          const response = await getComments(postid);
          if (response.status === "success" && response.data?.comments) {
            const formattedComments = await formatCommentsWithLikes(
              response.data.comments
            );
            setComments(formattedComments);
          }
        } catch (error) {
          errorToast(
            "" +
              (error.response?.data?.message ??
                error.data?.message ??
                "Failed to load comments"),
            {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true
            }
          );
          console.error("Failed to fetch comments:", error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchComments();
    },
    [postid]
  );

  // Calculate total comments (including replies) and update parent component
  useEffect(
    () => {
      const calculateTotalComments = () => {
        let total = comments.length;
        comments.forEach(comment => {
          total += comment.replies ? comment.replies.length : 0;
        });
        return total;
      };

      const total = calculateTotalComments();
      onCommentCountChange(total);
    },
    [comments, onCommentCountChange]
  );

  // Helper function to format date
  const formatDate = dateString => {
    if (!dateString) return "Unknown date";

    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return "Just now";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? "day" : "days"} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Handle like/unlike functionality
  const handleLike = async commentId => {
    if (!isAuthenticated()) {
      navigate("/signin");
      return;
    }
    try {
      setComments(prevComments =>
        prevComments.map(comment => {
          if (comment.id === commentId) {
            return {
              ...comment,
              likes: comment.likedByCurrentUser
                ? comment.likes - 1
                : comment.likes + 1,
              likedByCurrentUser: !comment.likedByCurrentUser
            };
          }

          // Check if it's a reply
          if (comment.replies) {
            const updatedReplies = comment.replies.map(reply => {
              if (reply.id === commentId) {
                return {
                  ...reply,
                  likes: reply.likedByCurrentUser
                    ? reply.likes - 1
                    : reply.likes + 1,
                  likedByCurrentUser: !reply.likedByCurrentUser
                };
              }
              return reply;
            });

            return {
              ...comment,
              replies: updatedReplies
            };
          }

          return comment;
        })
      );

      // Call the API to toggle like
      await toggleCommentLike(commentId);
      const likeData = await fetchCommentLikes(commentId);
      setComments(prevComments =>
        prevComments.map(comment => {
          if (comment.id === commentId) {
            return {
              ...comment,
              likes: likeData.count,
              likedByCurrentUser: likeData.likedByCurrentUser
            };
          }

          // Check if it's a reply
          if (comment.replies) {
            const updatedReplies = comment.replies.map(reply => {
              if (reply.id === commentId) {
                return {
                  ...reply,
                  likes: likeData.count,
                  likedByCurrentUser: likeData.likedByCurrentUser
                };
              }
              return reply;
            });

            return {
              ...comment,
              replies: updatedReplies
            };
          }

          return comment;
        })
      );
    } catch (error) {
      const likeData = await fetchCommentLikes(commentId);

      setComments(prevComments =>
        prevComments.map(comment => {
          if (comment.id === commentId) {
            return {
              ...comment,
              likes: likeData.count,
              likedByCurrentUser: likeData.likedByCurrentUser
            };
          }

          if (comment.replies) {
            const updatedReplies = comment.replies.map(reply => {
              if (reply.id === commentId) {
                return {
                  ...reply,
                  likes: likeData.count,
                  likedByCurrentUser: likeData.likedByCurrentUser
                };
              }
              return reply;
            });

            return {
              ...comment,
              replies: updatedReplies
            };
          }

          return comment;
        })
      );

      errorToast("Failed to update like", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true
      });
      console.error("Failed to toggle like:", error);
    }
  };

  const addComment = async (text, image = null) => {
    if (!text.trim() && !image) return;

    if (!isAuthenticated()) {
      navigate("/signin");
      return;
    }
    const newComment = {
      id: Date.now(), // Temporary ID
      author: {
        name: "You", // This will be replaced when we refresh comments
        username: "user",
        avatar: null
      },
      content: text,
      timestamp: "Just now",
      likes: 0,
      likedByCurrentUser: false,
      replies: []
    };

    // Optimistically add comment to UI
    setComments([newComment, ...comments]);

    try {
      await postComments({ postid, comment: text, commentPhoto: image });

      successToast("Comment added successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true
      });

      // Refresh comments to get the updated list with server-generated IDs
      const updatedCommentsResponse = await getComments(postid);
      if (
        updatedCommentsResponse.status === "success" &&
        updatedCommentsResponse.data?.comments
      ) {
        const formattedComments = await formatCommentsWithLikes(
          updatedCommentsResponse.data.comments
        );
        setComments(formattedComments);
      }
    } catch (error) {
      // Remove the optimistically added comment
      setComments(comments);
      console.error("error", error);

      errorToast(
        "" + (error.response?.data?.message ?? error.data?.message ?? error),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true
        }
      );
    }
  };

  const addReply = async (commentId, text, image = null) => {
    if (!text.trim() && !image) return;

    try {
      // Call the same postComments API but with parentid
      const response = await postComments({
        postid: postid,
        comment: text,
        commentPhoto: image,
        parentid: commentId // Add the parent comment ID
      });

      successToast("Reply added successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true
      });

      // Refresh comments to get the updated list with server-generated IDs
      const updatedCommentsResponse = await getComments(postid);
      if (
        updatedCommentsResponse.status === "success" &&
        updatedCommentsResponse.data?.comments
      ) {
        const formattedComments = await formatCommentsWithLikes(
          updatedCommentsResponse.data.comments
        );
        setComments(formattedComments);
      }
    } catch (error) {
      errorToast(
        "" + (error.response?.data?.message ?? error.data?.message ?? error),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true
        }
      );
      console.error("Failed to post reply:", error);
    }
  };

  const handleDelete = async (
    commentId,
    isReply = false,
    parentCommentId = null
  ) => {
    // Store the comment/reply for potential rollback
    let deletedComment = null;
    let deletedReply = null;

    if (isReply && parentCommentId) {
      // Find and temporarily remove the reply
      const parentComment = comments.find(c => c.id === parentCommentId);
      deletedReply = parentComment?.replies?.find(r => r.id === commentId);

      // Optimistically remove reply from UI
      const updatedComments = comments.map(comment => {
        if (comment.id === parentCommentId) {
          return {
            ...comment,
            replies: comment.replies.filter(reply => reply.id !== commentId)
          };
        }
        return comment;
      });
      setComments(updatedComments);
    } else {
      // Find and temporarily remove the main comment
      deletedComment = comments.find(c => c.id === commentId);

      // Optimistically remove comment from UI
      setComments(comments.filter(comment => comment.id !== commentId));
    }

    try {
      const response = await removeComments(commentId);

      successToast("Comment deleted successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true
      });

      // Refresh comments to get the updated list from server
      const updatedCommentsResponse = await getComments(postid);
      if (
        updatedCommentsResponse.status === "success" &&
        updatedCommentsResponse.data?.comments
      ) {
        const formattedComments = await formatCommentsWithLikes(
          updatedCommentsResponse.data.comments
        );
        setComments(formattedComments);
      }
    } catch (error) {
      // Rollback the UI changes on error
      if (isReply && parentCommentId && deletedReply) {
        // Restore the deleted reply
        const updatedComments = comments.map(comment => {
          if (comment.id === parentCommentId) {
            return {
              ...comment,
              replies: [...(comment.replies || []), deletedReply]
            };
          }
          return comment;
        });
        setComments(updatedComments);
      } else if (deletedComment) {
        // Restore the deleted comment
        setComments([...comments, deletedComment]);
      }

      errorToast(
        "" +
          (error.response?.data?.message ??
            error.data?.message ??
            "Failed to delete comment"),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true
        }
      );
      console.error("Failed to delete comment:", error);
    }
  };

  return (
    <div ref={ref} className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-semibold mb-4">Comments</h3>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <CommentInput onSubmit={addComment} replyingTo="SharmaScoops" />
      </div>

      <div className="flex flex-col space-y-4 mt-6">
        {isLoading ? (
          <div className="text-center py-4">Loading comments...</div>
        ) : comments.length > 0 ? (
          comments.map(comment => (
            <Comment
              key={comment.id}
              comment={comment}
              postid={postid}
              scoopid={scoopid}
              onReply={addReply}
              onLike={handleLike}
              onDelete={handleDelete}
            />
          ))
        ) : (
          <div className="text-center py-4 text-gray-500">
            No comments yet. Be the first to comment!
          </div>
        )}
      </div>
    </div>
  );
});

CommentSection.displayName = "CommentSection";

export default CommentSection;
